import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronLeft,
    faDollarSign,
    faCalendarAlt,
    faPen,
    faCheck,
    faDownload,
    faPlus,
    faTimes,
    faUpload,
    faCreditCard,
    faHandshake,
    faReceipt,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import Footer from '../../components/Footer/Footer';
import NavInf from '../../components/NavInf/NavInf';
import { secureFetch } from '../../utils/secureFetch';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';
import Select from 'react-select';
import './DebtOperation.css';
import Loader from '../../components/Loader/Loader';
import Alert from '../../components/Alert/Alert';
import FirmaPad from '../../components/FirmaPad/FirmaPad';
import { generateAndDownloadPDF } from '../../components/PDFGenerator/PDFGenerator';
import { openWhatsApp } from '../../utils/whatsappHelper';

export default function DebtOperation() {
    const [providers, setProviders] = useState([]);
    const [provider, setProvider] = useState(null);
    const [selectedOption, setOption] = useState('abono');
    const [formData, setFormData] = useState({
        value: '',
        description: '',
        days: '',
        transferReference: '',
        otherMethodDescription: ''
    });
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState(null);
    const [firmaData, setFirmaData] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('presencial'); // presencial, transferencia, otro
    const [evidenceData, setEvidenceData] = useState(null); // Para evidencias alternativas

    // Estados para el flujo de éxito de abono
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [savedMovementData, setSavedMovementData] = useState(null);
    const [redirectTimeoutId, setRedirectTimeoutId] = useState(null);

    // Estados para el historial de movimientos
    const [providerHistory, setProviderHistory] = useState([]);
    const [showFullHistory, setShowFullHistory] = useState(false);

    // 1) Función para cargar proveedores
    const fetchProviders = async () => {
        const res = await secureFetch('https://api.minymol.com/debts/providers');
        if (!res.ok) throw new Error('Error cargando proveedores');
        const data = await res.json();
        setProviders(data);

        // Seleccionar proveedor desde URL si existe
        const urlParams = new URLSearchParams(window.location.search);
        const providerId = urlParams.get('providerId');
        if (providerId) {
            const selectedProvider = data.find(p => p.id === providerId);
            if (selectedProvider) {
                setProvider(selectedProvider);
                // Limpiar la URL para ocultar el providerId
                window.history.replaceState({}, '', window.location.pathname);
            }
        }

        setLoading(false);
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                fetchProviders();
            }
        });

        return () => unsubscribe();
    }, []);

    // 2) Selección de proveedor
    const handleSelectProvider = async opt => {
        if (!opt) {
            setProvider(null);
            setProviderHistory([]);
            setShowFullHistory(false);
            return;
        }
        const sel = providers.find(p => p.id === opt.value);
        setProvider(sel);
        
        // Cargar historial del proveedor
        try {
            const res = await secureFetch(`https://api.minymol.com/movements/by-provider/${opt.value}`);
            if (!res.ok) throw new Error('Error cargando historial');
            const json = await res.json();
            setProviderHistory(json.history || []);
        } catch (e) {
            console.error(e);
            setProviderHistory([]);
        }
    };

    const handleChange = e => {
        const { name, value } = e.target;

        if (name === 'value') {
            // Limpiar el valor: solo números
            const cleanValue = value.replace(/\D/g, '');
            setFormData(f => ({ ...f, [name]: cleanValue }));
        } else {
            setFormData(f => ({ ...f, [name]: value }));
        }
    };

    // Manejar cambio de método de pago
    const handlePaymentMethodChange = (method) => {
        setPaymentMethod(method);
        // Limpiar datos específicos del método anterior
        setFirmaData(null);
        setEvidenceData(null);
        setFormData(prev => ({
            ...prev,
            transferReference: '',
            otherMethodDescription: ''
        }));
    };

    // Manejar carga de evidencia (captura de pantalla, foto, etc.)
    const handleEvidenceUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validar que sea una imagen
            if (!file.type.startsWith('image/')) {
                setAlert({
                    type: 'error',
                    message: 'Solo se permiten archivos de imagen como evidencia',
                });
                return;
            }

            // Optimizar imagen manteniendo calidad
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                // Calcular dimensiones optimizadas (máximo 1200px de ancho)
                const maxWidth = 1200;
                const maxHeight = 900;
                let { width, height } = img;

                if (width > maxWidth) {
                    height = (height * maxWidth) / width;
                    width = maxWidth;
                }
                if (height > maxHeight) {
                    width = (width * maxHeight) / height;
                    height = maxHeight;
                }

                canvas.width = width;
                canvas.height = height;

                // Dibujar imagen optimizada
                ctx.drawImage(img, 0, 0, width, height);

                // Convertir a base64 con buena calidad
                const optimizedImageData = canvas.toDataURL('image/jpeg', 0.85);
                setEvidenceData(optimizedImageData);
            };

            img.src = URL.createObjectURL(file);
        }
    };

    // Función para formatear números con puntos
    const formatNumber = (num) => {
        if (!num) return '';
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    };

    const isValid = () => {
        if (!provider || !formData.value || (selectedOption === 'deuda' && !formData.days)) return false;
        if (selectedOption === 'abono' && parseFloat(formData.value) > provider.deudaRestante) return false;

        // Validación específica para abonos según el método de pago
        if (selectedOption === 'abono') {
            if (paymentMethod === 'presencial' && !firmaData) return false;
            if (paymentMethod === 'transferencia' && (!evidenceData || !formData.transferReference)) return false;
            if (paymentMethod === 'otro' && !formData.otherMethodDescription) return false;
        }

        return true;
    };

    // 3) Creación de movimiento
    const handleSubmit = async () => {
        if (!isValid()) return;
        try {
            const payload = {
                providerId: provider.id,
                type: selectedOption,
                amount: parseFloat(formData.value),
                description: formData.description,
                ...(selectedOption === 'deuda' && { dueInDays: parseInt(formData.days, 10) }),
                ...(selectedOption === 'abono' && {
                    paymentMethod,
                    ...(paymentMethod === 'transferencia' && { transferReference: formData.transferReference }),
                    ...(paymentMethod === 'otro' && { otherMethodDescription: formData.otherMethodDescription })
                }),
            };

            // Si es abono, guardar evidencia correspondiente en localStorage
            if (selectedOption === 'abono') {
                if (paymentMethod === 'presencial' && firmaData) {
                    localStorage.setItem('abonoSignature', firmaData);
                } else if (paymentMethod === 'transferencia' && evidenceData) {
                    localStorage.setItem('abonoEvidence', evidenceData);
                } else if (paymentMethod === 'otro' && evidenceData) {
                    localStorage.setItem('abonoEvidence', evidenceData);
                }
            }

            const res = await secureFetch('https://api.minymol.com/movements', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                setAlert({
                    type: 'error',
                    message: 'Error al registrar movimiento',
                });
                const err = await res.json();
                throw new Error(err.message || 'Error registrando movimiento');
            }

            // Refrescar lista de proveedores
            await fetchProviders();

            if (selectedOption === 'deuda') {
                // Flujo normal para deuda
                setAlert({
                    type: 'success',
                    message: 'Movimiento registrado correctamente',
                    redirectTo: null,
                });

                // Auto-redirect después de 3 segundos
                setTimeout(() => {
                    window.location.href = '/informe-deudas';
                }, 3000);
            } else {
                // Flujo especial para abono
                setSavedMovementData({
                    ...payload,
                    amount: parseFloat(formData.value),
                    // Incluir evidencia directamente en los datos guardados
                    evidenceData: paymentMethod === 'transferencia' ? evidenceData : null,
                    signatureData: paymentMethod === 'presencial' ? firmaData : null,
                });

                setShowSuccessModal(true);

                // Auto-redirect después de 8 segundos (cancelable)
                const timeoutId = setTimeout(() => {
                    // Limpiar localStorage antes de redirigir
                    localStorage.removeItem('abonoSignature');
                    localStorage.removeItem('abonoEvidence');
                    window.location.href = '/informe-deudas';
                }, 120000);
                setRedirectTimeoutId(timeoutId);
            }

            // Reset form (pero mantenemos evidenceData y firmaData para el PDF)
            setFormData({ value: '', description: '', days: '', transferReference: '', otherMethodDescription: '' });
            // NO reseteamos firmaData ni evidenceData aquí para que estén disponibles para el PDF
            setPaymentMethod('presencial');
        } catch (e) {
            console.error(e);
            setAlert({
                type: 'error',
                message: e.message || 'Error al registrar movimiento',
            });
        }
    };

    // Funciones para el modal de éxito
    const cancelRedirectTimeout = () => {
        if (redirectTimeoutId) {
            clearTimeout(redirectTimeoutId);
            setRedirectTimeoutId(null);
        }
    };

    const handleCloseModal = () => {
        cancelRedirectTimeout();
        // Limpiar localStorage al cerrar el modal
        localStorage.removeItem('abonoSignature');
        localStorage.removeItem('abonoEvidence');
        window.location.href = '/informe-deudas';
    };

    const handleDownloadPDF = async () => {
        cancelRedirectTimeout();
        try {
            await generateAndDownloadPDF(savedMovementData, provider);
        } catch (error) {
            setAlert({
                type: 'error',
                message: error.message,
            });
        }
    };

    const handleWhatsAppSend = () => {
        cancelRedirectTimeout();
        try {
            openWhatsApp(provider, savedMovementData.amount, savedMovementData.description, provider.deudaRestante);
        } catch (error) {
            setAlert({
                type: 'error',
                message: error.message,
            });
        }
    };

    const handleNewMovement = () => {
        cancelRedirectTimeout();
        // Limpiar localStorage al crear nuevo movimiento
        localStorage.removeItem('abonoSignature');
        localStorage.removeItem('abonoEvidence');
        setShowSuccessModal(false);
        setSavedMovementData(null);
        setProvider(null);
        setFormData({ value: '', description: '', days: '', transferReference: '', otherMethodDescription: '' });
        setOption('abono');
        setPaymentMethod('presencial');
        setFirmaData(null);
        setEvidenceData(null);
    };

    // Cleanup al desmontar el componente
    useEffect(() => {
        return () => {
            if (redirectTimeoutId) {
                clearTimeout(redirectTimeoutId);
            }
            // Limpiar localStorage al desmontar el componente
            localStorage.removeItem('abonoSignature');
            localStorage.removeItem('abonoEvidence');
        };
    }, [redirectTimeoutId]);

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            {alert && (
                <Alert
                    type={alert.type}
                    message={alert.message}
                    onClose={() => setAlert(null)}
                    redirectTo={alert.redirectTo}
                />
            )}

            <div className="debt-op-container">
                <div className={`debt-op-header ${selectedOption}`}>
                    <FontAwesomeIcon
                        icon={faChevronLeft}
                        className={`back-button ${selectedOption}`}
                        onClick={() => (window.history.back())}
                    />
                    <h2>Registrar movimiento</h2>
                </div>

                <div className="debt-op-form">
                    <label>Proveedor</label>
                    <Select
                        options={providers.map(p => ({ value: p.id, label: p.name }))}
                        value={provider ? { value: provider.id, label: provider.name } : null}
                        onChange={handleSelectProvider}
                        placeholder="Selecciona un proveedor"
                        className="provider-select"
                        classNamePrefix="select"
                        isClearable
                        noOptionsMessage={() => 'No hay proveedores disponibles. Ve a "Mis Proveedores" para agregar uno.'}
                    />

                    {providers.length === 0 && (
                        <div className="no-providers-message">
                            <p>No tienes proveedores registrados.</p>
                            <button
                                className="go-to-providers-btn"
                                onClick={() => (window.location.href = '/mis-proveedores')}
                            >
                                Ir a Mis Proveedores
                            </button>
                        </div>
                    )}

                    {provider && (
                        <>
                            <div className="provider-summary">
                                <div className="provider-info-column">
                                    <span className="provider-name">{provider.name}</span>
                                    <span className="debt-amount">${provider?.deudaRestante != null
                                        ? provider.deudaRestante.toLocaleString('es-CO')
                                        : '0'}</span>
                                </div>
                                <button
                                    className="whatsapp-provider-btn-large"
                                    onClick={() => {
                                        const clean = (provider.phone || "").replace(/\D/g, "");
                                        const normalized = clean.startsWith("57") ? clean : `57${clean}`;
                                        window.open(`https://wa.me/${normalized}`, '_blank');
                                    }}
                                    title="Escribir al proveedor"
                                >
                                    <FontAwesomeIcon icon={faWhatsapp} />
                                    <span>Escribir</span>
                                </button>
                            </div>

                            {providerHistory.length > 0 && (
                                <div className="provider-history-mini">
                                    <h3>Últimos movimientos</h3>
                                    <div className="history-list-mini">
                                        {(showFullHistory ? providerHistory : providerHistory.slice(0, 3)).map((item, idx) => (
                                            <div key={idx} className="history-item-mini">
                                                <div className="history-mini-left">
                                                    <span className="history-mini-date">{item.date}</span>
                                                    <span className="history-mini-type">
                                                        {item.type === 'Deuda nueva' && item.description ? item.description : item.type}
                                                    </span>
                                                </div>
                                                <div className="history-mini-right">
                                                    <span className={`history-mini-status ${item.status.toLowerCase()}`}>
                                                        {item.status}
                                                    </span>
                                                    <span className="history-mini-amount">
                                                        ${item.amount.toLocaleString()}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {providerHistory.length > 3 && (
                                        <button
                                            className="toggle-history-btn"
                                            onClick={() => setShowFullHistory(!showFullHistory)}
                                        >
                                            {showFullHistory ? 'Ver menos' : `Ver más (${providerHistory.length - 3})`}
                                        </button>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    <label>Tipo de movimiento</label>
                    <Select
                        options={[
                            { value: 'abono', label: 'Abonar' },
                            { value: 'deuda', label: 'Compra' }
                        ]}
                        value={{ value: selectedOption, label: selectedOption === 'deuda' ? 'Compra' : 'Abonar' }}
                        onChange={(opt) => setOption(opt.value)}
                        className={`movement-type-select ${selectedOption}`}
                        classNamePrefix="select"
                        isSearchable={false}
                    />

                    <div className="form">
                        <div className="input-group">
                            <FontAwesomeIcon
                                id={selectedOption === 'abono' ? 'btn-selected-abono' : 'btn-selected-deuda'}
                                icon={faDollarSign}
                            />
                            <input
                                type="text"
                                name="value"
                                placeholder="Valor del movimiento"
                                value={formatNumber(formData.value)}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="input-group">
                            <FontAwesomeIcon
                                id={selectedOption === 'abono' ? 'btn-selected-abono' : 'btn-selected-deuda'}
                                icon={faPen}
                            />
                            <input
                                type="text"
                                name="description"
                                placeholder="Descripción del movimiento"
                                value={formData.description}
                                onChange={handleChange}
                            />
                        </div>
                        {selectedOption === 'deuda' && (
                            <div className="input-group">
                                <FontAwesomeIcon
                                    id={selectedOption === 'abono' ? 'btn-selected-abono' : 'btn-selected-deuda'}
                                    icon={faCalendarAlt}
                                />
                                <input
                                    type="number"
                                    name="days"
                                    placeholder="Días para pagar"
                                    value={formData.days}
                                    onChange={handleChange}
                                />
                            </div>
                        )}
                    </div>

                    {selectedOption === 'abono' && (
                        <>
                            <label style={{ marginTop: '1rem' }}>Método de pago</label>
                            <div className="payment-method-selector">
                                <button
                                    type="button"
                                    className={`payment-method-btn ${paymentMethod === 'presencial' ? 'active' : ''}`}
                                    onClick={() => handlePaymentMethodChange('presencial')}
                                >
                                    <FontAwesomeIcon icon={faHandshake} />
                                    <span>Presencial</span>
                                </button>
                                <button
                                    type="button"
                                    className={`payment-method-btn ${paymentMethod === 'transferencia' ? 'active' : ''}`}
                                    onClick={() => handlePaymentMethodChange('transferencia')}
                                >
                                    <FontAwesomeIcon icon={faCreditCard} />
                                    <span>Transferencia</span>
                                </button>

                            </div>

                            {paymentMethod === 'presencial' && (
                                <>
                                    <label style={{ marginTop: '1rem' }}>Firma del proveedor</label>
                                    <FirmaPad onSave={setFirmaData} />
                                </>
                            )}

                            {paymentMethod === 'transferencia' && (
                                <>
                                    <div className="input-group" style={{ marginTop: '1rem' }}>
                                        <FontAwesomeIcon
                                            id="btn-selected-abono"
                                            icon={faReceipt}
                                        />
                                        <input
                                            type="text"
                                            name="transferReference"
                                            placeholder="Referencia de transferencia"
                                            value={formData.transferReference || ''}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <label style={{ marginTop: '1rem' }}>Evidencia de transferencia</label>
                                    <div className="evidence-upload">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleEvidenceUpload}
                                            style={{ display: 'none' }}
                                            id="evidence-upload"
                                        />
                                        <label htmlFor="evidence-upload" className="upload-btn">
                                            <FontAwesomeIcon icon={faUpload} />
                                            {evidenceData ? 'Cambiar evidencia' : 'Subir captura de pantalla'}
                                        </label>
                                        {evidenceData && (
                                            <div className="evidence-preview">
                                                <img src={evidenceData} alt="Evidencia" style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }} />
                                            </div>
                                        )}
                                    </div>
                                    <p className="evidence-help">
                                        📱 Sube una captura de pantalla del comprobante de transferencia. Esta imagen se incluirá en el PDF del comprobante.
                                    </p>
                                </>
                            )}
                        </>
                    )}

                    <button
                        className={`submit-btn ${selectedOption}`}
                        onClick={handleSubmit}
                        disabled={!isValid()}
                    >
                        Confirmar movimiento
                    </button>
                </div>
                {showSuccessModal && (
                    <div className="success-modal-overlay">
                        <div className="success-modal">
                            <button className="close-btn" onClick={handleCloseModal}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>

                            <div className="success-content">
                                <div className="check-animation">
                                    <FontAwesomeIcon icon={faCheck} />
                                </div>
                                <h2>¡Abono registrado exitosamente!</h2>
                                <p>Se ha registrado el abono por ${savedMovementData?.amount?.toLocaleString('es-CO')} para {provider?.name}</p>

                                <div className="action-buttons">
                                    <button className="pdf-btn" onClick={handleDownloadPDF}>
                                        <FontAwesomeIcon icon={faDownload} />
                                        Descargar comprobante PDF
                                    </button>

                                    <button className="whatsapp-btn-debt" onClick={handleWhatsAppSend}>
                                        <FontAwesomeIcon icon={faWhatsapp} />
                                        Escribir al proveedor
                                    </button>

                                    <button className="new-movement-btn" onClick={handleNewMovement}>
                                        <FontAwesomeIcon icon={faPlus} />
                                        Registrar otro movimiento
                                    </button>
                                </div>

                                <p className="auto-redirect-text">
                                    Serás redirigido automáticamente en unos segundos...
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <NavInf />
            <Footer navinf={true} />
        </>
    );
}
